// ==============================
// File: src/components/Screens/InspirationPage.jsx
// Adds: Import images + Camera capture (fail-soft storage)
// Unified persistence for meta (categories) & notes
// Files:
//   - inspiration/meta.json   (per-image category/tag-like meta)
//   - inspiration/notes.json  (sticky note positions + text)
//   - inspiration/user.json   (user-imported/captured images, fail-soft)
// Rehydrates after "mudo:vault-restored"
// ==============================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInspiration } from "../../contexts/InspirationContext";
import { useStorage } from "../../contexts/StorageContext";

const DEFAULT_CATEGORIES = ["Glazes", "Forms", "Handles", "Textures", "Packaging"];
const NOTE_COLORS = ["#BDE0FE", "#CDE8D5", "#FFE8A3", "#F9B9B7", "#D9C7F3", "#B8F2E6"];

const FILE_META = "inspiration/meta.json";
const FILE_NOTES = "inspiration/notes.json";
const FILE_USER = "inspiration/user.json"; // local user images list

// Legacy localStorage keys (for one-time migration, if present)
const LS_NOTES = "mudo:inspo:notes";
const LS_META = "mudo:inspo:meta";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const rid = (p = "id") =>
  `${p}_${Math.random().toString(36).slice(2, 6)}${Date.now()
    .toString(36)
    .slice(-4)}`;

export default function InspirationPage() {
  // Inspiration context (may not provide all fns; fail-soft)
  const insp = useInspiration();
  const { items = [], remove, clear } = insp || {};

  const { loadJSON, saveJSON } = useStorage();

  const [activeCat, setActiveCat] = useState("All");
  const [metaById, setMetaById] = useState({});
  const [notesById, setNotesById] = useState({});

  // Locally managed list (if context has no add() or for imports/camera)
  const [userItems, setUserItems] = useState([]); // [{id,url,thumb,author,source}]
  const allItems = useMemo(
    () => [...(items || []), ...userItems],
    [items, userItems]
  );

  // ---- hydrate meta + notes + user images (with one-time localStorage migration)
  useEffect(() => {
    let alive = true;

    async function hydrate() {
      try {
        let meta = await loadJSON(FILE_META, null);
        let notes = await loadJSON(FILE_NOTES, null);
        let user = await loadJSON(FILE_USER, null);

        // One-time migration from legacy localStorage if files are empty
        if (!meta || typeof meta !== "object") {
          try {
            const raw = localStorage.getItem(LS_META);
            if (raw) meta = JSON.parse(raw);
          } catch {}
        }
        if (!notes || typeof notes !== "object") {
          try {
            const raw = localStorage.getItem(LS_NOTES);
            if (raw) notes = JSON.parse(raw);
          } catch {}
        }
        if (!Array.isArray(user)) user = [];

        meta = meta && typeof meta === "object" ? meta : {};
        notes = notes && typeof notes === "object" ? notes : {};

        if (alive) {
          setMetaById(meta);
          setNotesById(notes);
          setUserItems(user);
        }

        // Write forward so future loads skip legacy path
        try {
          await saveJSON(FILE_META, meta);
        } catch {}
        try {
          await saveJSON(FILE_NOTES, notes);
        } catch {}
        try {
          await saveJSON(FILE_USER, user);
        } catch {}
      } catch {
        if (alive) {
          setMetaById({});
          setNotesById({});
          setUserItems([]);
        }
      }
    }

    hydrate();

    const onRestored = async () => {
      try {
        const meta = (await loadJSON(FILE_META, {})) || {};
        const notes = (await loadJSON(FILE_NOTES, {})) || {};
        const user = (await loadJSON(FILE_USER, [])) || [];
        setMetaById(meta);
        setNotesById(notes);
        setUserItems(user);
      } catch {}
    };
    window.addEventListener("mudo:vault-restored", onRestored);

    return () => {
      alive = false;
      window.removeEventListener("mudo:vault-restored", onRestored);
    };
  }, [loadJSON, saveJSON]);

  // ---- persist (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      saveJSON(FILE_META, metaById).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [metaById, saveJSON]);

  useEffect(() => {
    const t = setTimeout(() => {
      saveJSON(FILE_NOTES, notesById).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [notesById, saveJSON]);

  useEffect(() => {
    const t = setTimeout(() => {
      saveJSON(FILE_USER, userItems).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [userItems, saveJSON]);

  // ---- clean orphaned meta/notes when images change
  useEffect(() => {
    const ids = new Set(allItems.map((i) => String(i.id)));
    setMetaById((prev) => {
      const next = {};
      for (const k in prev) if (ids.has(String(k))) next[k] = prev[k];
      return next;
    });
    setNotesById((prev) => {
      const next = {};
      for (const k in prev) if (ids.has(String(k))) next[k] = prev[k];
      return next;
    });
  }, [allItems]);

  // ---- filter
  const shown = useMemo(() => {
    if (activeCat === "All") return allItems;
    return allItems.filter(
      (img) =>
        (metaById[String(img.id)]?.category || DEFAULT_CATEGORIES[0]) ===
        activeCat
    );
  }, [allItems, activeCat, metaById]);

  // ---- meta helpers
  const setCategory = (id, cat) =>
    setMetaById((p) => ({
      ...p,
      [String(id)]: { ...(p[String(id)] || {}), category: cat },
    }));

  // ---- note helpers
  const addNote = (id, x, y, text = "") => {
    const color = NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)];
    const sid = String(id);
    setNotesById((p) => ({
      ...p,
      [sid]: [
        ...(p[sid] || []),
        { id: rid("note"), x, y, text, color, tags: [], collapsed: false },
      ],
    }));
  };
  const updateNote = (id, nid, patch) => {
    const sid = String(id);
    setNotesById((p) => ({
      ...p,
      [sid]: (p[sid] || []).map((n) =>
        n.id === nid ? { ...n, ...patch } : n
      ),
    }));
  };
  const removeNote = (id, nid) => {
    const sid = String(id);
    setNotesById((p) => ({
      ...p,
      [sid]: (p[sid] || []).filter((n) => n.id !== nid),
    }));
  };

  // ---- robust remove (string-normalized + fail-soft)
  const handleRemove = (id) => {
    const sid = String(id);

    // Try context removal but don't block UI on error
    try {
      const maybe = remove?.(id);
      if (maybe && typeof maybe.then === "function") {
        maybe.catch(() => {});
      }
    } catch {
      /* ignore */
    }

    // Always update local UI state
    setUserItems((xs) => xs.filter((x) => String(x.id) !== sid));

    // Purge meta + notes
    setMetaById((p) => {
      const n = { ...p };
      delete n[sid];
      return n;
    });
    setNotesById((p) => {
      const n = { ...p };
      delete n[sid];
      return n;
    });
  };

  const handleClear = () => {
    try {
      clear?.();
    } catch {}
    setUserItems([]);
    setMetaById({});
    setNotesById({});
  };

  // ---- Import (files) + Camera capture
  async function filesToDataUrls(fileList) {
    const arr = Array.from(fileList || []);
    const urls = await Promise.all(
      arr.map(
        (f) =>
          new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(String(r.result || ""));
            r.onerror = rej;
            r.readAsDataURL(f);
          })
      )
    );
    return urls.map((u, i) => ({
      url: u,
      name: arr[i]?.name || `import-${i + 1}.jpg`,
    }));
  }

  function addImageObjects(objs, source = "import") {
    const payloads = objs.map((o) => ({
      id: rid("user"),
      url: o.url,
      thumb: o.url,
      author: "You",
      source,
    }));
    if (typeof insp?.add === "function") {
      payloads.forEach((p) => {
        try {
          insp.add(p);
        } catch {}
      });
    } else {
      setUserItems((xs) => [...payloads, ...xs]);
    }
  }

  async function handleImport(e) {
    const files = e.target?.files;
    if (!files || !files.length) return;
    const data = await filesToDataUrls(files);
    addImageObjects(data, "import");
    e.target.value = "";
  }

  function onCameraSaved(dataUrl) {
    addImageObjects([{ url: dataUrl, name: "capture.jpg" }], "camera");
  }

  return (
    <div 
      className="min-h-screen p-6 text-neutral-900 dark:text-white transition-colors" 
      style={{ backgroundColor: 'var(--color-app-bg)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Saved Inspiration</h1>
        <div className="flex items-center gap-2">
          <CategoryChips
            categories={["All", ...DEFAULT_CATEGORIES]}
            active={activeCat}
            onPick={setActiveCat}
          />

          {/* Import & Camera */}
          <ImportControls onFiles={handleImport} />
          <CameraButton onSaved={onCameraSaved} />

          <Link
            to="/dashboard"
            className="px-3 py-1 rounded-lg text-white text-sm shadow transition-colors"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {allItems.length === 0 ? (
        <div className="text-center mt-32 opacity-70 text-sm">
          No saved images yet.
          <div className="mt-2">
            Use Import/Camera above, or go to Dashboard and click “Save”.
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={handleClear}
              className="text-xs px-3 py-1 rounded-full bg-red-500/80 hover:bg-red-600 text-white shadow transition-colors"
            >
              Clear All
            </button>
          </div>

          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {shown.map((img) => (
              <InspoCard
                key={img.id}
                img={img}
                category={
                  metaById[String(img.id)]?.category || DEFAULT_CATEGORIES[0]
                }
                notes={notesById[String(img.id)] || []}
                onSetCategory={(c) => setCategory(img.id, c)}
                onAddNote={(x, y, t) => addNote(img.id, x, y, t)}
                onUpdateNote={(nid, patch) => updateNote(img.id, nid, patch)}
                onRemoveNote={(nid) => removeNote(img.id, nid)}
                onRemove={() => handleRemove(img.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Import (files) ---------- */
function ImportControls({ onFiles }) {
  const fileRef = useRef(null);
  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFiles}
      />
      <button
        className="px-3 py-1.5 text-sm rounded-lg bg-white/80 dark:bg-white/10 text-black dark:text-white border border-black/10 dark:border-white/10 hover:bg-white dark:hover:bg-white/20 transition-colors"
        onClick={() => fileRef.current?.click()}
        title="Import images"
      >
        Import
      </button>
    </div>
  );
}

/* ---------- Camera capture ---------- */
function CameraButton({ onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="px-3 py-1.5 text-sm rounded-lg bg-white/80 dark:bg-white/10 text-black dark:text-white border border-black/10 dark:border-white/10 hover:bg-white dark:hover:bg-white/20 transition-colors"
        onClick={() => setOpen(true)}
        title="Open camera"
      >
        Camera
      </button>
      {open && (
        <CameraModal
          onClose={() => setOpen(false)}
          onSaved={(d) => {
            onSaved?.(d);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function CameraModal({ onClose, onSaved }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facing, setFacing] = useState("environment"); // or "user"
  const [shot, setShot] = useState(null);

  useEffect(() => {
    let active = true;
    async function start() {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing },
          audio: false,
        });
        if (!active) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
      } catch {
        onClose?.();
      }
    }
    start();
    return () => {
      active = false;
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  function capture() {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.92);
    setShot(dataUrl);
  }

  function save() {
    if (shot) onSaved?.(shot);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 dark:border-white/10">
          <div className="text-sm font-medium">Capture photo</div>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 text-xs rounded border border-black/10 dark:border-white/20"
              onClick={() =>
                setFacing((f) => (f === "user" ? "environment" : "user"))
              }
            >
              Flip
            </button>
            <button
              className="px-2 py-1 text-xs rounded border border-black/10 dark:border-white/20"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-3">
          {!shot ? (
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black/50">
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                playsInline
                muted
              />
            </div>
          ) : (
            <img
              src={shot}
              alt="preview"
              className="w-full rounded-lg object-contain"
            />
          )}

          <canvas ref={canvasRef} className="hidden" />

          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs opacity-70">
              Photos save locally in your Inspiration vault.
            </div>
            <div className="flex items-center gap-2">
              {!shot ? (
                <button
                  className="px-3 py-1.5 rounded bg-black text-white"
                  onClick={capture}
                >
                  Capture
                </button>
              ) : (
                <>
                  <button
                    className="px-3 py-1.5 rounded border border-black/10 dark:border-white/20"
                    onClick={() => setShot(null)}
                  >
                    Retake
                  </button>
                  <button
                    className="px-3 py-1.5 rounded bg-black text-white"
                    onClick={save}
                  >
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- UI: Category Chips ---------- */
function CategoryChips({ categories, active, onPick }) {
  return (
    <div
      className="flex items-center gap-1 p-1 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white/70 dark:bg-neutral-800/70 backdrop-blur-sm"
      role="tablist"
      aria-label="Filter by category"
    >
      {categories.map((c) => (
        <button
          key={c}
          role="tab"
          aria-selected={active === c}
          className={`px-3 py-1.5 text-sm rounded-xl transition ${
            active === c
              ? "bg-black text-white dark:bg-white dark:text-black"
              : "hover:bg-neutral-200/60 dark:hover:bg-neutral-700/60"
          }`}
          onClick={() => onPick(c)}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

/* ---------- UI: Card ---------- */
function InspoCard({
  img,
  category,
  notes,
  onSetCategory,
  onAddNote,
  onUpdateNote,
  onRemoveNote,
  onRemove,
}) {
  const ref = useRef(null);

  const addCenter = () => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const x = Math.max(0, box.width / 2 - 100);
    const y = Math.max(0, box.height / 2 - 60);
    onAddNote(x, y, "");
  };

  const onDouble = (e) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    onAddNote(e.clientX - box.left - 100, e.clientY - box.top - 50, "");
  };

  const sourceHref =
    img.authorUrl || img.sourceUrl || img.pageUrl || img.url || "";

  return (
    <figure
      ref={ref}
      className="relative rounded-2xl overflow-hidden shadow-md border border-black/10 dark:border-white/10 bg-white dark:bg-neutral-900"
      onDoubleClick={onDouble}
    >
      <img
        src={img.thumb || img.url}
        alt={img.author || "Inspiration"}
        className="w-full h-64 object-cover select-none pointer-events-none"
        draggable={false}
      />

      {/* Top controls */}
      <div className="absolute inset-x-0 top-0 p-2 flex items-center justify-between z-30">
        <div className="flex items-center gap-2">
          <select
            className="text-[11px] px-2 py-1 rounded-lg bg-white/85 text-black border border-white/60 shadow-sm hover:bg-white focus:outline-none"
            value={category}
            onChange={(e) => onSetCategory(e.target.value)}
            aria-label="Select category"
          >
            {DEFAULT_CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <span className="px-2 py-1 text-[11px] rounded-lg bg-black/50 text-white/90 backdrop-blur-sm truncate max-w-[140px]">
            {img.author || img.source || "Pexels"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addCenter}
            className="px-2 py-1 text-xs rounded-lg bg-white/85 text-black hover:bg-white border border-white/60 shadow-sm"
            title="Add note"
            type="button"
          >
            +
          </button>
          <a
            href={sourceHref || undefined}
            target="_blank"
            rel="noreferrer"
            className={`px-2 py-1 text-[11px] rounded-lg ${
              sourceHref
                ? "bg-white/85 text-black hover:bg-white border border-white/60 shadow-sm"
                : "bg-white/40 text-black/50 cursor-not-allowed"
            }`}
            onClick={(e) => {
              if (!sourceHref) e.preventDefault();
            }}
            aria-disabled={!sourceHref}
          >
            Source
          </a>
        </div>
      </div>

      {/* Notes layer */}
      <div className="absolute inset-0 z-20">
        {notes.map((n) => (
          <NoteBubble
            key={n.id}
            note={n}
            containerRef={ref}
            onChange={(p) => onUpdateNote(n.id, p)}
            onDelete={() => onRemoveNote(n.id)}
          />
        ))}
      </div>

      {/* Bottom chrome — raised above notes so Remove is clickable */}
      <figcaption className="absolute inset-x-0 bottom-0 p-2 text-xs text-white/90 bg-gradient-to-t from-black/60 to-transparent flex justify-between items-center z-40">
        <span className="truncate">{img.author || img.source || "Pexels"}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          className="text-[11px] px-2 py-1 rounded-full bg-white/85 text-black shadow hover:bg-white"
          title="Remove image"
        >
          Remove
        </button>
      </figcaption>
    </figure>
  );
}

/* ---------- UI: Note Bubble ---------- */
function NoteBubble({ note, onChange, onDelete, containerRef }) {
  const { x, y, text, color = NOTE_COLORS[0], tags = [], collapsed = false } =
    note;
  const [editing, setEditing] = useState(text === "");
  const [draft, setDraft] = useState(text || "");
  const [tagInput, setTagInput] = useState("");
  const drag = useRef({ dragging: false, sx: 0, sy: 0, bx: x, by: y });

  function beginDrag(e) {
    const el = e.target;
    if (el && el.closest && el.closest("button, input, textarea, select, a"))
      return;
    drag.current = {
      dragging: true,
      sx: e.clientX,
      sy: e.clientY,
      bx: note.x,
      by: note.y,
    };
    window.addEventListener("pointermove", onDrag);
    window.addEventListener("pointerup", endDrag, { once: true });
  }
  function onDrag(e) {
    if (!drag.current.dragging) return;
    const c = containerRef.current;
    if (!c) return;
    const box = c.getBoundingClientRect();
    the
    const maxW = box.width - 260;
    const maxH = box.height - (collapsed ? 48 : 160);
    const nx = clamp(
      drag.current.bx + (e.clientX - drag.current.sx),
      0,
      Math.max(0, maxW)
    );
    const ny = clamp(
      drag.current.by + (e.clientY - drag.current.sy),
      0,
      Math.max(0, maxH)
    );
    onChange({ x: nx, y: ny });
  }
  function endDrag() {
    drag.current.dragging = false;
    window.removeEventListener("pointermove", onDrag);
  }

  function save() {
    onChange({ text: draft.trim() });
    setEditing(false);
  }
  function addTag(t) {
    const clean = t.trim().replace(/^#/, "");
    if (!clean) return;
    const next = Array.from(new Set([...(tags || []), clean]));
    onChange({ tags: next });
    setTagInput("");
  }
  function removeTag(t) {
    onChange({ tags: tags.filter((x) => x !== t) });
  }
  function toggleCollapse() {
    onChange({ collapsed: !collapsed });
  }

  const glassTint = `${color}40`;
  const preview = (text || "").trim().slice(0, 24) || "note";

  return (
    <motion.div
      className="absolute"
      style={{ left: x, top: y, pointerEvents: "auto" }}
      initial={{ y: -6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
    >
      {collapsed ? (
        <div
          onPointerDown={beginDrag}
          className="h-9 px-3 rounded-full flex items-center gap-2 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-white/30 dark:ring-white/10"
          style={{
            background: `linear-gradient( to bottom right, rgba(255,255,255,.18), rgba(255,255,255,.06) ), ${glassTint}`,
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-black/30" />
          <span className="text-xs text-black/85 dark:text-white/90">
            {preview}…
          </span>
          <button
            className="ml-1 text-[11px] px-2 py-0.5 rounded-full ring-1 ring-white/30 hover:bg-white/40"
            onClick={toggleCollapse}
          >
            Expand
          </button>
          <button
            className="text-[11px] px-2 py-0.5 rounded-full ring-1 ring-white/30 hover:bg-white/40"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      ) : (
        <div
          onPointerDown={beginDrag}
          className="w-[260px] rounded-3xl overflow-hidden backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-white/30 dark:ring-white/10"
          style={{
            background: `linear-gradient( to bottom right, rgba(255,255,255,.18), rgba(255,255,255,.06) ), ${glassTint}`,
          }}
        >
          <div className="flex items-center justify-between px-3 pt-2">
            <div className="flex gap-1.5">
              {NOTE_COLORS.map((c) => (
                <button
                  key={c}
                  className="w-4.5 h-4.5 rounded-full ring-1 ring-white/40"
                  style={{
                    background: c,
                    boxShadow:
                      color === c
                        ? "0 0 0 2px rgba(0,0,0,0.25) inset"
                        : undefined,
                  }}
                  onClick={() => onChange({ color: c })}
                  title={c}
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button
                className="text-[11px] px-2 py-0.5 rounded-full ring-1 ring-white/30 hover:bg-white/40"
                onClick={toggleCollapse}
              >
                Minimize
              </button>
              <button
                className="text-[11px] px-2 py-0.5 rounded-full ring-1 ring-white/30 hover:bg-white/40"
                onClick={onDelete}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="p-3">
            {editing ? (
              <div className="space-y-2">
                <textarea
                  className="w-full h-20 text-[12px] rounded-2xl p-2 outline-none bg-white/30 dark:bg-white/10 ring-1 ring-white/30 focus:ring-2 focus:ring-white/50 placeholder:text-black/50 dark:placeholder:text-white/60"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a note…"
                />
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 text-[11px] rounded-full bg-black text-white"
                    onClick={save}
                  >
                    Save
                  </button>
                  <button
                    className="px-3 py-1 text-[11px] rounded-full ring-1 ring-white/30 hover:bg-white/40"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-[13px] leading-snug text-black/90 dark:text-white/90 whitespace-pre-wrap">
                  {text || <span className="opacity-60">(empty)</span>}
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(tags || []).map((t) => (
                    <span
                      key={t}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-white/35 text-black/85 dark:text-white/90 ring-1 ring-white/40"
                    >
                      #{t}
                      <button
                        className="ml-1 opacity-70 hover:opacity-100"
                        onClick={() => removeTag(t)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <div className="mt-2 flex items-center gap-1.5">
                  <input
                    className="flex-1 text-[11px] px-2 py-1 rounded-full outline-none bg-white/30 dark:bg-white/10 ring-1 ring-white/30 focus:ring-2 focus:ring-white/50"
                    placeholder="Add tag…"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (addTag(tagInput), setTagInput(""))
                    }
                  />
                  <button
                    className="px-3 py-1 text-[11px] rounded-full ring-1 ring-white/30 hover:bg-white/40"
                    onClick={() => (addTag(tagInput), setTagInput(""))}
                  >
                    Add
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
